import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format, startOfDay, addMonths } from 'date-fns';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CalendarIcon,
  Loader2,
  Plus,
  Trash2,
  Upload,
  FileText,
  Image,
  ImageIcon,
  X,
  Edit2,
} from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import createApiClient from '@/api/axiosInstance';
import PartModal from '@/js/components/modals/PartModal';
import { Label } from '@/components/ui/label';
import { clearSearchAction } from '@/js/reduxstore/actions/searchActions';
import { saveProjectDetailsAction } from '@/js/reduxstore/actions/projectActions';
import FileUpload from '@/js/components/FileUpload';
import { fetchExistingDocumentsArray } from '@/js/components/FileUploadBox';

const FORM_STATE_ENUM = {
  CREATING_PROJECT: 'CREATING_PROJECT',
  DISPLAY_PROJECT: 'DISPLAY_PROJECT',
  EDIT_FORM: 'EDIT_FORM',
};

const PART_FORM_STATE_ENUM = {
  CREATING_PART: 'CREATING_PART',
  DISPLAY_PART: 'DISPLAY_PART',
  EDIT_FORM: 'EDIT_FORM',
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

const formSchema = z.object({
  project_name: z.string().min(1, 'Project name is required'),
  product_description: z.string().min(1, 'Product Description is required'),
  project_due_date: z.date({
    required_error: 'Due date is required',
    invalid_type_error: 'Invalid due date',
  }),
  quantity_to_manufacture: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  length: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  width: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  height: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  dimension_uom: z.string().optional(),
  product_weight: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  weight_uom: z.string().optional(),
});

const ProjectDescriptionScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const apiClient = createApiClient(navigate, dispatch);
  const token = useSelector((state) => state.authReducer.token);

  const { projectId, projectName } = useParams();
  const [projectData, setProjectData] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formState, setFormState] = useState(() => {
    if (projectId) return FORM_STATE_ENUM.DISPLAY_PROJECT;
    return FORM_STATE_ENUM.CREATING_PROJECT;
  });

  const isDisabled = formState === FORM_STATE_ENUM.DISPLAY_PROJECT;
  const isEditing = formState === FORM_STATE_ENUM.EDIT_FORM;

  useEffect(() => {
    if (location.state?.state) {
      setFormState(location.state.state);
    } else if (projectId) {
      setFormState(FORM_STATE_ENUM.DISPLAY_PROJECT);
    } else {
      setFormState(FORM_STATE_ENUM.CREATING_PROJECT);
    }
  }, [location.state, projectId]);

  // ShortlistedManufacturers
  const reduxShortlistData = useSelector(
    (state) => state?.searchReducer?.shortlisted,
  );
  const shortlisted = reduxShortlistData?.shortlisted;

  // PARTS
  const [parts, setParts] = useState([]);
  const [showPartModal, setShowPartModal] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partFormState, setPartFormState] = useState(
    PART_FORM_STATE_ENUM.CREATING_PART,
  );

  const handleAddPart = (partData) => {
    if (selectedPart !== null) {
      const updatedParts = [...parts];
      updatedParts[selectedPart.index] = partData;
      setParts(updatedParts);
    } else {
      setParts((prev) => [...prev, partData]);
    }

    // Reset modal state
    setShowPartModal(false);
    setSelectedPart(null);
  };

  const handleViewPart = (index, part) => {
    setSelectedPart({ index: index, part: part });
    setPartFormState(PART_FORM_STATE_ENUM.DISPLAY_PART);
    setShowPartModal(true);
  };

  const handleDeletePart = (index) => {
    const updatedParts = [...parts];
    updatedParts.splice(index, 1);
    setParts(updatedParts);
  };

  useEffect(() => {
    const getProjectData = async () => {
      try {
        const response = await apiClient.post(
          '/api/project-part/get',
          JSON.stringify({
            project_id: projectId,
          }),
        );

        const responseData = response.data;
        if (responseData?.data) {
          setProjectData(responseData?.data?.project);
          setParts(responseData?.data?.parts);

          dispatch(saveProjectDetailsAction(responseData?.data));

          form.reset({
            project_name: responseData?.data?.project.project_name || '',
            product_description: responseData?.data.project.description || '',
            project_due_date: responseData?.data.project.due_date
              ? new Date(responseData?.data.project.due_date)
              : undefined,
            quantity_to_manufacture:
              responseData?.data.project.total_quantity_to_manufacture ||
              undefined,
            length: responseData?.data.project.product_length || undefined,
            width: responseData?.data.project.product_width || undefined,
            height: responseData?.data.project.product_height || undefined,
            dimension_uom:
              responseData?.data.project.product_dimension_uom || '',
            product_weight:
              responseData?.data.project.product_weight || undefined,
            weight_uom: responseData?.data.project.product_weight_uom || '',
          });
        }
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.error('Error: ', error_message || error);
      }
    };

    if (projectId) {
      setFormState(FORM_STATE_ENUM.DISPLAY_PROJECT);
      getProjectData();
    }
  }, []);

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      project_name: projectData?.project_name || '',
      product_description: projectData?.description || '',
      project_due_date: projectData?.due_date
        ? new Date(projectData.due_date)
        : undefined,
      quantity_to_manufacture:
        projectData?.total_quantity_to_manufacture || undefined,
      length: projectData?.product_length || undefined,
      width: projectData?.product_width || undefined,
      height: projectData?.product_height || undefined,
      dimension_uom: projectData?.product_dimension_uom || '',
      product_weight: projectData?.product_weight || undefined,
      weight_uom: projectData?.product_weight_uom || '',
    },
  });

  const due_date = useWatch({ control: form.control, name: 'due_date' });

  const disableInvalidDates = (date) => {
    const today = startOfDay(new Date());
    const sixMonthsFromNow = addMonths(today, 6);
    return date < today || date > sixMonthsFromNow;
  };

  // for document upload
  const getDocumentTitles = () => {
    let suffix = '';

    if (formState !== FORM_STATE_ENUM.CREATING_PROJECT) {
      suffix = projectId ? `${projectId}` : '';
    }

    return {
      nda: `ProjectNDA-${suffix}`,
      otherDocument: `ProjectOtherDocument-${suffix}`,
    };
  };

  const DOCUMENT_TITLES = getDocumentTitles();

  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [documentStates, setDocumentStates] = useState({
    otherDocument: null,
  });
  const [uploadProgress, setUploadProgress] = useState({
    otherDocument: 0,
  });

  useEffect(() => {
    fetchExistingDocumentsArray(
      projectData?.document_ids,
      apiClient,
      setExistingDocuments,
      setError,
    );
  }, [token]);

  const onSubmit = async (values) => {
    setIsLoading(true);

    if (formState === FORM_STATE_ENUM.CREATING_PROJECT) {
      const docIDs = existingDocuments
        .filter(
          (document) =>
            document.document_name === 'ProjectNDA-' ||
            document.document_name === 'ProjectOtherDocument-',
        )
        .map((document) => document.id);
      const formattedValues = {
        project_name: values.project_name,
        due_date: values.project_due_date
          ? format(new Date(values.project_due_date), 'yyyy-MM-dd')
          : '',
        description: values.product_description,
        total_quantity_to_manufacture: values.quantity_to_manufacture,
        product_length: values.length,
        product_width: values.width,
        product_height: values.height,
        product_dimension_uom: values.dimension_uom,
        product_weight: values.product_weight,
        product_weight_uom: values.weight_uom,
        document_ids: docIDs,
        status: 'In Progress',
      };
      // console.log('formattedValues' + formattedValues);

      try {
        // Step 1: Create the project
        const response = await apiClient.post(
          '/api/project/create',
          JSON.stringify(formattedValues),
        );
        const projectData = response.data?.data;
        const projectId = projectData?.project_id;
        const projectName = projectData?.project_name;
        const projectVersionId = projectData?.project_version_id;

        console.log(
          '✅ Project creation successful! with data:',
          response.data,
        );
        setError(null);

        // Step 2: Link parts with project
        try {
          const partIds = parts.map((part) => part.part_id);

          const linkingResponse = await apiClient.post(
            '/api/project-part/create',
            JSON.stringify({
              project_id: projectId,
              part_ids: partIds,
            }),
          );

          console.log(
            '✅ Part and project linked successful! with data:',
            linkingResponse.data,
          );
        } catch (linkError) {
          // Log error but continue with the process
          console.log(linkError?.response?.data?.error_message || linkError);
        }

        // DOCUMENT UPDAATE
        const project_nda_doc = existingDocuments.find(
          (document) => document.document_name === 'ProjectNDA-',
        );
        const project_other_document_doc = existingDocuments.find(
          (document) => document.document_name === 'ProjectOtherDocument-',
        );

        // updating document ids
        if (project_nda_doc) {
          const partImageUpdateResponse = await apiClient.post(
            '/api/documents/update',
            JSON.stringify({
              document_id: project_nda_doc?.id,
              document_name: `ProjectNDA-${projectId}`,
            }),
          );
          const projectNdaUpdateResponseData = partImageUpdateResponse.data;
          // console.log('✅ Project NDA name updated:', projectNdaUpdateResponseData);
        }

        if (project_other_document_doc) {
          const otherDocumentUpdateResponse = await apiClient.post(
            '/api/documents/update',
            JSON.stringify({
              document_id: project_other_document_doc?.id,
              document_name: `ProjectOtherDocument-${projectId}`,
            }),
          );
          const otherDocumentUpdateResponseData =
            otherDocumentUpdateResponse.data;
          console.log(
            '✅ Other Doccument name updated:',
            otherDocumentUpdateResponseData,
          );
        }

        // Step 3: Handle shortlisted manufacturers (if any)
        if (shortlisted && shortlisted.length > 0) {
          try {
            const manufacturerIds = shortlisted.map(
              (manu) => manu.manufacturer_id,
            );

            const shortlistResponse = await apiClient.post(
              '/api/shortlisted-manufacturer/create',
              JSON.stringify({
                project_id: projectId,
                project_version_id: projectVersionId,
                part_ids: [],
                part_version_ids: [],
                manufacturer_ids: manufacturerIds,
              }),
            );

            console.log(
              '✅ Manufacturers shortlisted successfully:',
              shortlistResponse.data,
            );
          } catch (shortlistError) {
            // Log error but continue with the process
            console.log(
              shortlistError?.response?.data?.error_message || shortlistError,
            );
          }
        }
        // Step 6: Clear Search Redux data
        dispatch(clearSearchAction());

        // Step 5: Navigate to project details page
        navigate(`/project/details/${projectName}/${projectId}`, {
          state: {
            state: 'DISPLAY_PROJECT',
          },
        });
      } catch (error) {
        // Handle main error that would prevent project creation
        const errorMessage = error?.response?.data?.error_message;
        console.log(errorMessage || error);

        setError(
          errorMessage ||
            'A network error occurred. Please check your connection and try again.',
        );
      } finally {
        setIsLoading(false);
      }
    } else if (formState === FORM_STATE_ENUM.EDIT_FORM) {
      const docIDs = existingDocuments
        .filter(
          (document) =>
            document.document_name === `ProjectNDA-${projectId}` ||
            document.document_name === `ProjectOtherDocument-${projectId}`,
        )
        .map((document) => document.id);

      const formattedValues = {
        project_id: projectId,
        project_name: values.project_name,
        due_date: values.project_due_date
          ? format(new Date(values.project_due_date), 'yyyy-MM-dd')
          : '',
        description: values.product_description,
        total_quantity_to_manufacture: values.quantity_to_manufacture,
        product_length: values.length,
        product_width: values.width,
        product_height: values.height,
        product_dimension_uom: values.dimension_uom,
        product_weight: values.product_weight,
        product_weight_uom: values.weight_uom,
        document_ids: docIDs,
        status: 'In Progress',
      };
      console.log(formattedValues);

      try {
        const response = await apiClient.put(
          '/api/project/update',
          JSON.stringify(formattedValues),
        );
        const responseData = response.data;
        const projectId = responseData?.data?.project_id;
        const projectName = responseData?.data?.project_name;

        console.log('✅ Project creation successful! with data:', responseData);
        setError(null);

        try {
          let partIds = [];
          partIds = parts.map((part) => part.part_id);

          //linking part with project
          const linkingResponse = await apiClient.post(
            '/api/project-part/create',
            JSON.stringify({
              project_id: projectId,
              part_ids: partIds,
            }),
          );
          const linkingResponseData = linkingResponse.data;
          console.log(
            '✅ Part and project linked successful! with data:',
            linkingResponseData,
          );
          setError(null);

          navigate(`/project/details/${projectName}/${projectId}`, {
            state: {
              state: 'DISPLAY_PROJECT',
            },
          });
          console.log('WEHAVE navigate');
        } catch (error) {
          const error_message = error?.response?.data?.error_message;
          console.log(error_message || error);
        }
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.log(error_message || error);

        setError(
          error_message ||
            'A network error occurred. Please check your connection and try again.',
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div
      className="bg-white rounded-lg font-roboto max-h-[calc(100vh-60px)] overflow-y-auto"
      style={{
        scrollbarWidth: 'none',
        scrollbarColor: '#7636D9 #f0f0f0',
      }}
    >
      <div className="py-6 px-10">
        {/* Form Section */}
        <div className="">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  e.target.tagName === 'INPUT' &&
                  e.target.tagName !== 'TEXTAREA' &&
                  !e.target.getAttribute('data-allow-enter')
                ) {
                  e.preventDefault();
                }
              }}
              className=""
            >
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-lg font-medium">Project Details</h1>

                <div className="flex gap-4">
                  {formState === FORM_STATE_ENUM.DISPLAY_PROJECT && (
                    <Button
                      variant="outline"
                      type="submit"
                      className={`w-[100px] rounded text-base text-white transition ease-in-out duration-300 bg-darkPurple hover:bg-darkerPurple hover:text-white flex items-center justify-center`}
                      onClick={() => {
                        navigate(
                          `/project/createRFQ/${projectName}/${projectId}`,
                        );
                      }}
                    >
                      Send RFQ
                    </Button>
                  )}
                  {formState !== FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                    <Button
                      variant="outline"
                      type="submit"
                      disabled={isLoading}
                      className={`w-[150px] text-base text-white transition ease-in-out duration-300 ${
                        isLoading
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-darkPurple hover:bg-darkerPurple hover:text-white'
                      } flex items-center justify-center`}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Project'
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      disabled={isLoading}
                      className={`w-[90px] text-base text-white transition ease-in-out duration-300 ${
                        isLoading
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-darkPurple hover:bg-darkerPurple hover:text-white'
                      } flex items-center justify-center`}
                      onClick={(e) => {
                        e.preventDefault();
                        setFormState(FORM_STATE_ENUM.EDIT_FORM);
                      }}
                    >
                      <Edit2 className="h-4 w-4" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>

              {/* Initial Information */}
              <div className="bg-gray-50 p-6 rounded-lg text-gray-600 mr-2">
                {/* Project Name */}
                <FormField
                  control={form.control}
                  name="project_name"
                  render={({ field }) => (
                    <FormItem className="w-1/2 group flex items-center gap-6">
                      <FormLabel className="pt-2 group-hover:text-darkPurple">
                        Project Name
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                          <p className="text-sm text-black px-3 py-2 rounded font-semibold">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Input
                            className="w-[200px] focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none  placeholder:text-gray-400 border border-gray-300 group-hover:border-darkPurple"
                            placeholder="Enter Project Name"
                            {...field}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Product Description */}
                <FormField
                  control={form.control}
                  name="product_description"
                  render={({ field }) => (
                    <FormItem className="my-6 group">
                      <FormLabel className="group-hover:text-darkPurple">
                        Product Description
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                          <p className="min-h-[80px] w-full text-sm text-black bg-gray-100 px-3 py-2 rounded w-[200px]">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Textarea
                            className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-darkPurple"
                            placeholder="Describe the product"
                            {...field}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Project Due Date */}
                <FormField
                  control={form.control}
                  name="project_due_date"
                  render={({ field }) => (
                    <FormItem className="w-1/3 my-6 group">
                      <FormLabel className="group-hover:text-darkPurple">
                        Project Due Date
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                          <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                            {field.value ? format(field.value, 'PPP') : '—'}
                          </p>
                        ) : (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={`w-full flex justify-between ease-in duration-150 ${
                                  field.value ? '' : 'text-gray-400'
                                } font-normal border border-gray-300`}
                              >
                                {field.value
                                  ? format(field.value, 'PPP')
                                  : 'Select the project due date'}
                                <CalendarIcon className="ml-2 h-4 w-4 text-darkPurple" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={disableInvalidDates}
                                classNames={{
                                  caption_label:
                                    'text-sm font-medium text-gray-600',
                                  nav: 'space-x-1 flex items-center',
                                  nav_button:
                                    'h-7 w-7 bg-transparent p-0 text-gray-500 hover:text-gray-700',
                                  nav_button_previous: 'absolute left-1',
                                  nav_button_next: 'absolute right-1',
                                  table: 'w-full border-collapse space-y-1',
                                  head_row:
                                    'flex bg-veryLightPurple bg-opacity-15 rounded',
                                  head_cell:
                                    'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] h-9 flex items-center justify-center',
                                  row: 'flex w-full mt-2',
                                  cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-purple-50',
                                  day: 'h-9 w-9 p-0 font-normal text-gray-600 hover:bg-purple-50 hover:text-purple-700',
                                  day_selected:
                                    'bg-purple-100 text-purple-900 hover:bg-purple-200 hover:text-purple-900 focus:bg-purple-200 focus:text-purple-900',
                                  day_today:
                                    'bg-gray-100 text-gray-900 font-semibold',
                                  day_outside:
                                    'text-gray-400 opacity-50 hover:bg-transparent hover:text-gray-400 hover:cursor-default',
                                  day_disabled:
                                    'text-gray-400 opacity-50 hover:bg-transparent cursor-not-allowed',
                                  day_range_middle:
                                    'aria-selected:bg-purple-50 aria-selected:text-purple-900',
                                  day_hidden: 'invisible',
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Product Details Section */}
              <div className="bg-gray-50 p-6 rounded-lg text-gray-500 mt-16 mr-2">
                <h2 className="text-darkPurple font-medium text-lg">
                  Product Details
                </h2>

                <FormField
                  control={form.control}
                  name="quantity_to_manufacture"
                  render={({ field }) => (
                    <FormItem className="my-6 w-1/2 group ease-in duration-300">
                      <FormLabel className="group-hover:text-lightPurple">
                        Total Quantity to Manufacture
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                          <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded w-[200px]">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Input
                            {...field}
                            type="number"
                            step="1"
                            placeholder="Enter the required number of parts (e.g., 100, 10,000)"
                            className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              );
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="my-6">
                  <p className=" text-sm text-gray-500 font-medium">
                    Overall Dimensions
                  </p>
                  <div className="grid grid-cols-7 gap-2">
                    {['length', 'width', 'height'].map((name) => (
                      <FormField
                        key={name}
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <FormItem className="my-2 col-span-2 group ease-in duration-300">
                            <FormControl>
                              {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                                <p className="flex gap-2 w-full text-sm text-black bg-gray-100 px-3 py-2 rounded w-[200px]">
                                  {field.value || '—'}
                                  <p className="text-gray-400">{` (${name})`}</p>
                                </p>
                              ) : (
                                <Input
                                  {...field}
                                  type="number"
                                  step="1"
                                  className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                  onChange={(e) => {
                                    field.onChange(
                                      e.target.value === ''
                                        ? ''
                                        : Number(e.target.value),
                                    );
                                  }}
                                  onKeyDown={(e) => {
                                    if (['e', 'E', '+', '-'].includes(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onWheel={(e) => e.target.blur()}
                                  placeholder={`Item ${name.replace('item', '').toLowerCase()}`}
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ))}

                    <FormField
                      control={form.control}
                      name="dimension_uom"
                      render={({ field }) => (
                        <FormItem className="my-2 group ease-in duration-300">
                          <FormControl>
                            {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                              <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                                {field.value ? field.value.toUpperCase() : '—'}
                              </p>
                            ) : (
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <SelectTrigger className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                                  {field.value || (
                                    <span className="text-slate-500">UOM</span>
                                  )}
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem
                                    value="smm"
                                    className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                  >
                                    MM
                                  </SelectItem>
                                  <SelectItem
                                    value="cm"
                                    className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                  >
                                    CM
                                  </SelectItem>
                                  <SelectItem
                                    value="m"
                                    className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                  >
                                    M
                                  </SelectItem>
                                  <SelectItem
                                    value="inch"
                                    className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                  >
                                    IN
                                  </SelectItem>
                                  <SelectItem
                                    value="feet"
                                    className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                  >
                                    FT
                                  </SelectItem>
                                  <SelectItem
                                    value="yard"
                                    className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                  >
                                    YD
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Product Weight */}
                <div className="my-6 w-2/3">
                  <p className="text-sm text-gray-500 font-medium">
                    Product Weight
                  </p>

                  <div className="grid grid-cols-4 gap-2">
                    <FormField
                      control={form.control}
                      name="product_weight"
                      render={({ field }) => (
                        <FormItem className="my-2 col-span-3 group ease-in duration-300">
                          <FormControl>
                            {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                              <p className=" w-full text-sm text-black bg-gray-100 px-3 py-2 rounded w-[200px]">
                                {field.value || '—'}
                              </p>
                            ) : (
                              <Input
                                {...field}
                                type="number"
                                step="1"
                                placeholder="Enter Weight"
                                className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                onChange={(e) => {
                                  field.onChange(
                                    e.target.value === ''
                                      ? ''
                                      : Number(e.target.value),
                                  );
                                }}
                                onKeyDown={(e) => {
                                  if (['e', 'E', '+', '-'].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                onWheel={(e) => e.target.blur()}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="weight_uom"
                      render={({ field }) => (
                        <FormItem className="my-2 group ease-in duration-300">
                          {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                            <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded w-[200px]">
                              {field.value || '—'}
                            </p>
                          ) : (
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                                  {field.value || (
                                    <span className="text-slate-500">UOM</span>
                                  )}
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem
                                  value="milli gram"
                                  className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                >
                                  MG
                                </SelectItem>
                                <SelectItem
                                  value="gram"
                                  className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                >
                                  GM
                                </SelectItem>
                                <SelectItem
                                  value="kilo gram"
                                  className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                >
                                  KG
                                </SelectItem>
                                <SelectItem
                                  value="pounds"
                                  className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                >
                                  LB
                                </SelectItem>
                                <SelectItem
                                  value="ounce"
                                  className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                >
                                  OZ
                                </SelectItem>
                                <SelectItem
                                  value="tone"
                                  className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                >
                                  T
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Parts Details Section */}
              <div className="bg-gray-50 p-6 rounded-lg text-gray-500 mt-16 mr-2">
                <h2 className="text-darkPurple font-medium text-lg">
                  Parts Details
                </h2>

                {parts && parts.length > 0 ? (
                  <>
                    <div className="mt-10 grid grid-cols-6 gap-4 text-black text-md border py-4 rounded-md px-2 bg-veryVeryLightPurple">
                      <div className="ml-3 col-span-1">Image</div>
                      <div className="col-span-1">Part Name</div>
                      <div className="col-span-1 text-center">Part Number</div>
                      <div className="col-span-1 text-center">
                        Part Revision
                      </div>
                      <div className="col-span-1 text-center">Quantity</div>
                      <div className="col-span-1 text-center">Action</div>
                    </div>

                    {parts.map((part, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-6 gap-4 mt-3 items-center py-4 text-sm bg-gray-50 rounded-md px-2 bg-white shadow"
                      >
                        <div className="ml-3 col-span-1">
                          <img
                            src={part.image}
                            alt={part.part_name}
                            className="w-16 h-16 bg-gray-100 rounded shadow"
                          />
                        </div>
                        <div className="col-span-1 ">{part.part_name}</div>
                        <div className="col-span-1 text-center">
                          {part.part_number}
                        </div>
                        <div className="col-span-1 text-center">
                          {part.part_revision}
                        </div>
                        <div className="col-span-1 text-center">
                          {part.total_parts_required}
                        </div>
                        <div className="flex justify-center gap-2">
                          <button
                            className={` text-purple-700 hover:bg-veryLightPurple hover:bg-opacity-10 ease-in duration-200 px-2 py-1 rounded border border-lightPurple  ${formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? '' : 'w-full'}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleViewPart(index, part);
                            }}
                          >
                            View{' '}
                            {formState === FORM_STATE_ENUM.DISPLAY_PROJECT &&
                              'Details'}
                          </button>
                          {formState !== FORM_STATE_ENUM.DISPLAY_PROJECT && (
                            <button
                              className="w-full flex gap-2 text-red-700 hover:bg-red-100 ease-in duration-200 px-2 py-1 rounded border border-red-700"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDeletePart(index);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="mt-10 grid grid-cols-6 gap-4 text-black text-md border py-4 rounded-md px-2 bg-veryVeryLightPurple">
                      <div className="ml-3 col-span-1 ">Image</div>
                      <div className="col-span-1 ">Part Name</div>
                      <div className="col-span-1 text-center">Part Number</div>
                      <div className="col-span-1 text-center">
                        Part Revision
                      </div>
                      <div className="col-span-1 text-center">Quantity</div>
                      <div className="col-span-1 text-center">Action</div>
                    </div>
                    <p className="text-gray-500 mt-4 text-center">
                      You have no parts available. Proceed to add a part
                    </p>
                  </>
                )}

                {formState !== FORM_STATE_ENUM.DISPLAY_PROJECT && (
                  <button
                    className={`mt-8 text-base text-darkPurple font-medium transition ease-in-out duration-300 hover:bg-slate-100 flex items-center gap-2 px-2 py-1 border border-darkPurple rounded`}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPartModal(true);
                    }}
                  >
                    <Plus className="h-6 w-6" />
                    Add Parts
                  </button>
                )}
              </div>

              {/* Upload Documents */}
              <div className="bg-gray-50 p-6 rounded-lg text-gray-500 mt-16 mr-2">
                <h2 className="text-darkPurple font-medium text-lg">
                  Upload Documents
                </h2>
                <p className="text-xs mt-1">
                  Upload any relevant files to provide additional context for
                  manufacturers.
                </p>
                <p className="text-xs mt-1">
                  Supported file types: PDF, CAD, DOC, Max size: 20MB per file
                </p>

                <FormField
                  control={form.control}
                  name="otherDocument"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <FormControl>
                        <div className="flex gap-2">
                          {/* NDA DOC */}
                          {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                            existingDocuments.some(
                              (doc) =>
                                doc.document_name === DOCUMENT_TITLES.nda,
                            ) ? (
                              <FileUpload
                                DOCUMENT_TITLES={DOCUMENT_TITLES}
                                MAX_FILE_SIZE={MAX_FILE_SIZE}
                                ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                                existingDocuments={existingDocuments}
                                setExistingDocuments={setExistingDocuments}
                                documentStates={documentStates}
                                setDocumentStates={setDocumentStates}
                                uploadProgress={uploadProgress}
                                setUploadProgress={setUploadProgress}
                                isUploading={isUploading}
                                setIsUploading={setIsUploading}
                                setError={setError}
                                editState={
                                  formState === FORM_STATE_ENUM.EDIT_FORM
                                }
                                key="nda"
                                id="nda"
                                label="Upload NDA"
                                documentType={DOCUMENT_TITLES.nda}
                                classNames="w-52 h-10"
                                existingDocument={existingDocuments.find(
                                  (doc) =>
                                    doc.document_name === DOCUMENT_TITLES.nda,
                                )}
                              />
                            ) : (
                              <p className="w-52 h-10 text-sm text-black bg-gray-100 px-3 py-2 rounded">
                                {field.value || 'No NDA'}
                              </p>
                            )
                          ) : (
                            <FileUpload
                              DOCUMENT_TITLES={DOCUMENT_TITLES}
                              MAX_FILE_SIZE={MAX_FILE_SIZE}
                              ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                              existingDocuments={existingDocuments}
                              setExistingDocuments={setExistingDocuments}
                              documentStates={documentStates}
                              setDocumentStates={setDocumentStates}
                              uploadProgress={uploadProgress}
                              setUploadProgress={setUploadProgress}
                              isUploading={isUploading}
                              setIsUploading={setIsUploading}
                              setError={setError}
                              key="nda"
                              id="nda"
                              label="Upload NDA"
                              documentType={DOCUMENT_TITLES.nda}
                              classNames="w-52 h-10"
                              existingDocument={existingDocuments.find(
                                (doc) =>
                                  doc.document_name === DOCUMENT_TITLES.nda,
                              )}
                            />
                          )}

                          {formState === FORM_STATE_ENUM.DISPLAY_PROJECT ? (
                            existingDocuments.some(
                              (doc) =>
                                doc.document_name ===
                                DOCUMENT_TITLES.otherDocument,
                            ) ? (
                              <FileUpload
                                DOCUMENT_TITLES={DOCUMENT_TITLES}
                                MAX_FILE_SIZE={MAX_FILE_SIZE}
                                ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                                existingDocuments={existingDocuments}
                                setExistingDocuments={setExistingDocuments}
                                documentStates={documentStates}
                                setDocumentStates={setDocumentStates}
                                uploadProgress={uploadProgress}
                                setUploadProgress={setUploadProgress}
                                isUploading={isUploading}
                                setIsUploading={setIsUploading}
                                setError={setError}
                                editState={
                                  formState === FORM_STATE_ENUM.EDIT_FORM
                                }
                                key="otherDocument"
                                id="otherDocument"
                                label="Upload Documents"
                                documentType={DOCUMENT_TITLES.otherDocument}
                                classNames="w-52 h-10"
                                existingDocument={existingDocuments.find(
                                  (doc) =>
                                    doc.document_name ===
                                    DOCUMENT_TITLES.otherDocument,
                                )}
                              />
                            ) : (
                              <p className="w-52 h-10 text-sm text-black bg-gray-100 px-3 py-2 rounded">
                                {field.value || 'No Other Document'}
                              </p>
                            )
                          ) : (
                            <FileUpload
                              DOCUMENT_TITLES={DOCUMENT_TITLES}
                              MAX_FILE_SIZE={MAX_FILE_SIZE}
                              ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                              existingDocuments={existingDocuments}
                              setExistingDocuments={setExistingDocuments}
                              documentStates={documentStates}
                              setDocumentStates={setDocumentStates}
                              uploadProgress={uploadProgress}
                              setUploadProgress={setUploadProgress}
                              isUploading={isUploading}
                              setIsUploading={setIsUploading}
                              setError={setError}
                              key="otherDocument"
                              id="otherDocument"
                              label="Upload Documents"
                              documentType={DOCUMENT_TITLES.otherDocument}
                              classNames="w-52 h-10"
                              existingDocument={existingDocuments.find(
                                (doc) =>
                                  doc.document_name ===
                                  DOCUMENT_TITLES.otherDocument,
                              )}
                            />
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Submission Deadline Section */}
              {/* <div className="bg-gray-50 p-6 rounded-lg text-gray-500 mt-16">
                <h2 className="text-darkPurple font-medium text-lg">
                  Specify a Submission Deadline
                </h2>
                <p className="text-xs mt-1">
                  Define the last date and time for manufacturers to respond.
                </p>

                <FormField
                  control={form.control}
                  name="submission_deadline"
                  render={({ field }) => (
                    <FormItem className="w-1/3 my-6">
                      <FormLabel>Deadline for Submission</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={`w-full flex justify-between ease-in duration-150 ${
                                field.value ? '' : 'text-slate-500'
                              } font-normal border border-gray-300`}
                            >
                              {field.value
                                ? format(field.value, 'PPP')
                                : 'Select date'}
                              <CalendarIcon className="ml-2 h-4 w-4 text-darkPurple" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent>
                          <Calendar
                            mode="single"
                            classNames={{
                              caption_label:
                                'text-sm font-medium text-gray-600',
                              nav: 'space-x-1 flex items-center',
                              nav_button:
                                'h-7 w-7 bg-transparent p-0 text-gray-500 hover:text-gray-700',
                              nav_button_previous: 'absolute left-1',
                              nav_button_next: 'absolute right-1',
                              table: 'w-full border-collapse space-y-1',
                              head_row:
                                'flex bg-veryLightPurple bg-opacity-15 rounded',
                              head_cell:
                                'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] h-9 flex items-center justify-center',
                              row: 'flex w-full mt-2',
                              cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-purple-50',
                              day: 'h-9 w-9 p-0 font-normal text-gray-600 hover:bg-purple-50 hover:text-purple-700',
                              day_selected:
                                'bg-purple-100 text-purple-900 hover:bg-purple-200 hover:text-purple-900 focus:bg-purple-200 focus:text-purple-900',
                              day_today:
                                'bg-gray-100 text-gray-900 font-semibold',
                              day_outside:
                                'text-gray-400 opacity-50 hover:bg-transparent hover:text-gray-400 hover:cursor-default',
                              day_disabled:
                                'text-gray-400 opacity-50 hover:bg-transparent cursor-not-allowed',
                              day_range_middle:
                                'aria-selected:bg-purple-50 aria-selected:text-purple-900',
                              day_hidden: 'invisible',
                            }}
                            selected={field.value}
                            onSelect={field.onChange}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div> */}

              {error && (
                <div className="w-full flex items-center justify-center mt-4 ">
                  <p className="bg-[#F8D7D7] text-[#B64444] text-sm py-3 px-3 rounded-lg">
                    {error}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <div className="mt-6 w-full flex items-center justify-center gap-4">
                {formState !== FORM_STATE_ENUM.DISPLAY_PROJECT && (
                  <Button
                    variant="outline"
                    type="submit"
                    disabled={isLoading}
                    className={`w-[150px] text-base text-white transition ease-in-out duration-300 ${
                      isLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-darkPurple hover:bg-darkerPurple hover:text-white'
                    } flex items-center justify-center`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Project'
                    )}
                  </Button>
                )}
              </div>
            </form>
          </Form>

          <PartModal
            totalQuantityToManufacture={
              form.getValues().quantity_to_manufacture
            }
            selectedPart={selectedPart}
            open={showPartModal}
            state={partFormState}
            onClose={() => {
              setShowPartModal(false);
              setSelectedPart(null);
            }}
            onSubmit={(data) => handleAddPart(data)}
          />

          {/* {showPartModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
              <div
                className="bg-white rounded-lg shadow-lg p-6 max-w-[80%] max-h-[95%] overflow-y-auto"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#7636D9 #f0f0f0',
                }}
              >
                <PartDetailsScreen2
                  partData={selectedPart}
                  onClose={() => setShowPartModal(false)}
                  onSubmit={(data) => handleAddPart(data)}
                />
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default ProjectDescriptionScreen;
